import { useState } from 'react';
import { ChevronDown, ChevronRight, RotateCcw, Users } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

/**
 * LA COLONNE DES VALEURS — les champs que ce modèle sait remplir, et les autres repliés.
 *
 * ⚠️ ELLE NE CONNAISSAIT QUE CINQ CHAMPS, ET L'APERÇU MENTAIT DONC.
 *
 * Ce fichier portait une liste `JETONS` de cinq entrées, écrite en dur, quand le
 * catalogue en compte quatorze. Un opérateur qui posait une zone `Email` ne pouvait pas
 * la remplir ici, et le mode « Rempli » la montrait VIDE — alors que le mandat réel la
 * remplit depuis le dossier. La liste vient donc du serveur, comme celle de la palette :
 * une seconde liste tenue dans l'écran diverge au premier champ ajouté.
 *
 * ⚠️ ET CE QUE LE MODÈLE NE REMPLIRA PAS EST REPLIÉ, PAS SUPPRIMÉ.
 *
 * Premier réglage : on cachait les champs non couverts. Juste sur un mandat qui porte
 * ses libellés ; catastrophique sur une FACTURE importée comme gabarit — ni jeton, ni
 * libellé, ni case, donc aucun champ couvert, donc **zéro ligne de saisie**. Mesuré le
 * 10/09 en recette manuelle. Or c'est précisément le document où l'atelier sert à
 * quelque chose : c'est là qu'il faut poser des zones à la main, puisque rien ne se
 * devine.
 *
 * Le dépliant s'ouvre donc TOUT SEUL quand rien n'est couvert. Un panneau vide au moment
 * où on en a le plus besoin est pire qu'un panneau trop long.
 *
 * ⚠️ DEUX CHAMPS NE SE SAISISSENT PAS, ET CE N'EST PAS UN OUBLI.
 *
 * `Nom du client` et `Prénom et nom` sont CALCULÉS depuis `Prénom seul` + `Nom seul` :
 * `gabarits_mandat.contexte` et `gabarit_ancrage.valeurs_du_dossier` les recomposent en
 * production. Les laisser taper donnerait un champ qu'on remplit et que le document
 * ignore — « faire dire à l'application ce qu'elle ne sait pas ».
 *
 * ⚠️ UNE LIGNE PAR CHAMP, PAS TROIS. Trois lignes × cinq champs tenaient dans la
 * colonne, trois × quatorze n'y tiennent pas. Le nom du jeton passe en infobulle, et la
 * source devient une pastille.
 */

// La source de chaque champ, telle que `services/gabarit_couverture.couverture` la rend.
// La pastille dit d'un coup d'œil QUI remplira, ce que la ligne technique disait en mots.
const PASTILLES = {
  zone: { signe: '●', classe: 'text-marque', titre: 'posé à la main sur le plan' },
  jeton: { signe: '●', classe: 'text-marque', titre: 'rempli par un jeton {{…}} du document' },
  libelle: { signe: '○', classe: 'text-neutral-400', titre: 'rempli par le libellé du document' },
  aucune: { signe: '△', classe: 'text-amber-600', titre: 'ne sera PAS rempli par ce modèle' },
};

/** Ce que le serveur recomposera pour un champ calculé, montré ici à l'identique. */
function valeurCalculee(cle, valeurs) {
  const complet = [valeurs.prenom, valeurs.nom].filter(Boolean).join(' ').trim();
  return cle === 'client_nom' || cle === 'nom_complet' ? complet : '';
}

export default function ValeursDeLApercu({
  catalogue, jetons, valeurs, setValeurs, desactive, clients, choixClient,
  onOuvrirChoixClient, onChargerClient, onValeursExemple,
}) {
  const [deplie, setDeplie] = useState(false);
  const couverture = jetons?.couverture || {};
  const poser = (cle, valeur) => setValeurs((v) => ({ ...v, [cle]: valeur }));

  // ⚠️ LES TROIS FAMILLES SORTENT DU MÊME CATALOGUE, AUCUNE N'EST ÉCRITE ICI. Une liste
  // de cases tenue dans l'écran serait la même faute que la liste `JETONS` qu'on vient
  // d'enlever, en plus petit — et un consentement qui n'apparaît pas à l'écran est un
  // consentement qu'on ne relit jamais avant de faire signer.
  const parFamille = (famille) => (catalogue || []).filter((c) => c.famille === famille);
  const cases = parFamille('case');
  const signature = parFamille('signature');

  // ⚠️ ON N'AFFICHE PAS RIEN TANT QUE LA COUVERTURE N'EST PAS LÀ. Elle arrive du serveur
  // après le premier rendu : filtrer sur un objet vide viderait le panneau une fraction
  // de seconde, et un panneau qui clignote se lit comme un panneau cassé.
  const couvertureConnue = Object.keys(couverture).length > 0;
  const saisissables = parFamille('texte').filter((c) => !c.calcule);
  const remplira = (c) => !couvertureConnue || couverture[c.cle]?.source !== 'aucune';
  const utiles = saisissables.filter(remplira);
  const autres = saisissables.filter((c) => !remplira(c));
  const montrerLesAutres = deplie || utiles.length === 0;

  /** Une ligne : la pastille, le libellé tronqué, et le champ — ou la valeur calculée. */
  const ligne = ({ cle, libelle, exemple, calcule }) => {
    const pastille = PASTILLES[couverture[cle]?.source] || PASTILLES.aucune;
    return (
      <div key={cle} className="grid grid-cols-[11px_88px_1fr] items-center gap-1">
        <span className={`text-[10px] leading-none ${pastille.classe}`}
              title={pastille.titre} data-testid={`gabarit-source-${cle}`}>
          {pastille.signe}
        </span>
        {/* Le nom du jeton passe en infobulle : c'est la valeur qu'on veut voir dans le
            document, pas son étiquette technique. */}
        <span className="text-[10px] text-neutral-500 truncate"
              title={`${libelle} · {{${cle}}}`}>
          {libelle}
        </span>
        {calcule ? (
          <span className="text-[11px] text-neutral-400 truncate h-6 leading-6 px-1"
                title="Calculé depuis Prénom seul et Nom seul"
                data-testid={`gabarit-valeur-${cle}`}>
            {valeurCalculee(cle, valeurs) || '—'}
          </span>
        ) : (
          <Input
            className="h-6 text-[11px] px-1.5"
            placeholder={exemple}
            value={valeurs[cle] || ''}
            onChange={(e) => poser(cle, e.target.value)}
            disabled={desactive}
            data-testid={`gabarit-valeur-${cle}`}
          />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2 order-2 lg:order-3 overflow-y-auto pr-0.5"
         data-testid="gabarits-valeurs">
      <div>
        <p className="text-[12px] font-medium text-neutral-900">Valeurs de l&apos;aperçu</p>
        <p className="text-[10.5px] text-neutral-500 leading-tight">
          Elles ne changent que l&apos;aperçu. Un champ vide reprend l&apos;exemple.
        </p>
      </div>

      {/* ⚠️ LES DEUX BOUTONS D'ABORD. Remplir quatorze champs à la main n'est pas le geste
          courant : le geste courant est « montre-moi ce que ça donne ». */}
      <div className="flex gap-1.5">
        <Button variant="outline" size="sm" className="flex-1 text-[11px] h-7 px-1.5"
                onClick={onValeursExemple} disabled={desactive}
                data-testid="gabarit-valeurs-exemple">
          <RotateCcw className="w-3 h-3 mr-1" /> Exemple
        </Button>
        <Button variant="outline" size="sm" className="flex-1 text-[11px] h-7 px-1.5"
                onClick={onOuvrirChoixClient} disabled={desactive}
                data-testid="gabarit-charger-client">
          <Users className="w-3 h-3 mr-1" /> Client…
        </Button>
      </div>

      {choixClient && (
        <div className="rounded-lg border border-neutral-200 bg-white p-1.5 max-h-56 overflow-y-auto"
             data-testid="gabarit-liste-clients">
          {clients.length === 0 ? (
            <p className="text-[10.5px] text-neutral-400 p-1.5">Chargement…</p>
          ) : clients.map((c) => (
            <button type="button" key={c.id}
                    onClick={() => onChargerClient(c.id)}
                    className="w-full text-left px-1.5 py-1 rounded hover:bg-neutral-50 text-[11px]"
                    data-testid={`gabarit-client-${c.id}`}>
              <span className="text-neutral-900">
                {[c.prenom, c.nom].filter(Boolean).join(' ') || '—'}
              </span>
              <span className="text-neutral-400"> · {c.ville || '—'}</span>
            </button>
          ))}
        </div>
      )}

      <div className="space-y-0.5">{utiles.map(ligne)}</div>

      {autres.length > 0 && (
        <div className="border-t border-neutral-200 pt-1">
          <button
            type="button" onClick={() => setDeplie((d) => !d)}
            className="flex items-center gap-1 text-[10px] text-neutral-500 hover:text-neutral-700"
            data-testid="gabarit-autres-champs"
          >
            {montrerLesAutres ? <ChevronDown className="w-3 h-3" />
                              : <ChevronRight className="w-3 h-3" />}
            {autres.length} champ{autres.length > 1 ? 's' : ''} que ce modèle ne remplit pas
          </button>
          {montrerLesAutres && (
            <div className="space-y-0.5 mt-1">{autres.map(ligne)}</div>
          )}
        </div>
      )}

      {/* Les cases et la signature : le seul moyen de voir OÙ la croix et le paraphe
          tombent avant de faire signer un client. Le paraphe est un trait factice
          fabriqué par le serveur — on ne reprend jamais celui d'un vrai client. */}
      <div className="border-t border-neutral-200 pt-1.5 space-y-1">
        <div className="flex flex-wrap gap-x-2 gap-y-1">
          {cases.map(({ cle, libelle }) => (
            <label key={cle} className="flex items-center gap-1 text-[10.5px] text-neutral-600">
              <input type="checkbox" className="w-3 h-3 accent-marque"
                     checked={!!valeurs[cle]} disabled={desactive}
                     onChange={(e) => poser(cle, e.target.checked)}
                     data-testid={`gabarit-valeur-${cle}`} />
              {sansPrefixe(libelle)}
            </label>
          ))}
        </div>
        {signature.map(({ cle }) => (
          <label key={cle} className="flex items-center gap-1 text-[10.5px] text-neutral-600">
            <input type="checkbox" className="w-3 h-3 accent-marque"
                   checked={!!valeurs[cle]} disabled={desactive}
                   onChange={(e) => poser(cle, e.target.checked)}
                   data-testid={`gabarit-valeur-${cle}`} />
            signature présente
          </label>
        ))}
      </div>
    </div>
  );
}

// Le libellé du catalogue est « Case · modulation » : le préfixe sert à distinguer les
// familles dans la palette, il n'a plus de sens dans un bloc qui ne porte QUE des cases.
const sansPrefixe = (libelle) => libelle.replace(/^Case\s*·\s*/, '');
